import React, { useState, useEffect, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../config/firebase";
import {
  Database,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Clock,
  Users,
  FileQuestion,
  ArrowLeft,
  Loader2,
  ClipboardList,
  CheckCircle2,
  XCircle,
  UploadCloud,
  FileText,
  X,
} from "lucide-react";

// ─── Helpers ───────────────────────────────────────────────────────────────
const functions = getFunctions();
const smGetStoredResponses = httpsCallable(functions, "smGetStoredResponses");
const smIngestCSV = httpsCallable(functions, "smIngestCSV", { timeout: 300000 });

function formatDate(val) {
  if (!val) return "—";
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(val) {
  if (!val) return "—";
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ─── Match status badge ────────────────────────────────────────────────────
function MatchBadge({ status }) {
  const map = {
    matched: { label: "SIS Matched", color: "bg-emerald-100 text-emerald-800" },
    unmatched: { label: "Unmatched", color: "bg-amber-100 text-amber-800" },
    no_name: { label: "No Name", color: "bg-slate-100 text-slate-600" },
  };
  const { label, color } = map[status] || map.no_name;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

// ─── Ingest Log view ────────────────────────────────────────────────────────
function IngestLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(collection(db, "surveyIngests"));
        const results = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.ingestedAt?.toMillis?.() || 0) - (a.ingestedAt?.toMillis?.() || 0));
        setLogs(results);
      } catch (e) {
        setError(e.message || "Failed to load ingest log.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Group by surveyId to show latest ingest per survey + totals
  const bysurvey = logs.reduce((acc, log) => {
    const sid = log.surveyId || "unknown";
    if (!acc[sid]) acc[sid] = [];
    acc[sid].push(log);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={20} />
        Loading ingest log…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-3 text-sm text-red-700">
        <AlertCircle size={18} className="shrink-0 mt-0.5" />
        <p>{error}</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <ClipboardList size={32} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">No ingest records found.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        {Object.keys(bysurvey).length} survey{Object.keys(bysurvey).length !== 1 ? "s" : ""} ingested
        &middot; {logs.length} total ingest run{logs.length !== 1 ? "s" : ""}
      </p>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Survey ID</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Source</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Ingested At</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Processed</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Matched</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Unmatched</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">No Name</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">File</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {logs.map((log) => {
              const matchRate = log.processed > 0
                ? Math.round((log.matched / log.processed) * 100)
                : 0;
              return (
                <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{log.surveyId || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      log.source === "csv_import"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-purple-100 text-purple-800"
                    }`}>
                      {log.source === "csv_import" ? "CSV Import" : "API Pull"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{formatDateTime(log.ingestedAt)}</td>
                  <td className="px-4 py-3 text-xs text-slate-700 text-right font-medium">{log.processed?.toLocaleString() ?? 0}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-medium ${log.matched > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                      {log.matched?.toLocaleString() ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-medium ${log.unmatched > 0 ? "text-amber-600" : "text-slate-400"}`}>
                      {log.unmatched?.toLocaleString() ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 text-right">{log.noName?.toLocaleString() ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 truncate max-w-[180px]">{log.csvFile || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Survey list view — reads from Firestore, not SurveyMonkey API ─────────
function SurveyList({ onSelectSurvey }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, "surveys"));
      const results = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.questionMap || d.surveyId)
        .sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0));
      setSurveys(results);
    } catch (e) {
      setError(e.message || "Failed to load surveys from Firestore.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={20} />
        Loading ingested surveys…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-3 text-sm text-red-700">
        <AlertCircle size={18} className="shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Could not load surveys</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          {surveys.length} ingested survey{surveys.length !== 1 ? "s" : ""} stored in Firestore
        </p>
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
          <AlertCircle size={13} />
          SurveyMonkey API access inactive — showing stored data only
        </div>
      </div>

      {surveys.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <FileQuestion size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No ingested surveys found in Firestore.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
          {surveys.map((survey) => (
            <button
              key={survey.id}
              onClick={() => onSelectSurvey(survey)}
              className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4 group"
            >
              <div className="p-2 rounded-md bg-slate-100 group-hover:bg-white transition-colors">
                <Database size={16} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 text-sm truncate">{survey.title || survey.id}</p>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Users size={11} />
                    {survey.responseCount?.toLocaleString() ?? 0} responses
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={11} />
                    Last ingested {survey.updatedAt?.toDate
                      ? formatDate(survey.updatedAt.toDate().toISOString())
                      : "—"}
                  </span>
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Decode answers using questionMap ─────────────────────────────────────
function decodeAnswers(answers, questionMap) {
  if (!answers || !questionMap) return [];
  return Object.entries(answers).map(([qId, answerArr]) => {
    const q = questionMap[qId] || {};
    const heading = q.heading || qId;
    const isMatrix = q.type === "matrix" || (answerArr || []).some(a => a.row_id && a.choice_id);

    if (isMatrix) {
      // Group by row: each row is a sub-question
      const byRow = {};
      for (const a of (answerArr || [])) {
        const rowLabel = (a.row_id && q.rows?.[a.row_id]) || a.row_id || "?";
        const choiceLabel = (a.choice_id && q.choices?.[a.choice_id]) || a.text || a.choice_id || "?";
        if (!byRow[rowLabel]) byRow[rowLabel] = [];
        byRow[rowLabel].push(choiceLabel);
      }
      const decoded = Object.entries(byRow).map(([row, choices]) => ({
        row,
        value: choices.join(", "),
      }));
      return { qId, heading, isMatrix: true, decoded };
    }

    // Non-matrix: choice or text
    const decoded = (answerArr || []).map(a => {
      if (a.choice_id && q.choices?.[a.choice_id]) return q.choices[a.choice_id];
      if (a.text) return a.text;
      return null;
    }).filter(Boolean);
    return { qId, heading, isMatrix: false, decoded };
  }).filter(q => q.decoded.length > 0);
}

// ─── Expanded response detail panel ───────────────────────────────────────
function ResponseDetail({ response, questionMap, onClose }) {
  const decoded = decodeAnswers(response.answers, questionMap);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-800 text-sm">
              Response {response.smResponseId || response.id}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <MatchBadge status={response.matchStatus} />
              {response.sisId && (
                <span className="text-xs font-mono text-slate-500">SIS: {response.sisId}</span>
              )}
              <span className="text-xs text-slate-400">{formatDate(response.dateCreated)}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none ml-4">✕</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-4 space-y-4">
          {decoded.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              No decodable answers — choice IDs may not match the stored question map.
            </p>
          ) : (
            decoded.map(({ qId, heading, isMatrix, decoded: answers }) => (
              <div key={qId}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{heading}</p>
                {isMatrix ? (
                  <div className="rounded-lg border border-slate-100 overflow-hidden">
                    {answers.map(({ row, value }, i) => (
                      <div key={i} className={`flex items-center justify-between px-3 py-2 text-sm ${i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                        <span className="text-slate-600 flex-1 pr-4">{row}</span>
                        <span className="font-medium text-slate-900 shrink-0">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {answers.map((a, i) => (
                      <div key={i} className="text-sm text-slate-800 bg-slate-50 rounded px-3 py-2">
                        {a}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Response table ────────────────────────────────────────────────────────
function ResponseTable({ responses, questionMap }) {
  const [selected, setSelected] = useState(null);

  if (!responses || responses.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400 text-sm">
        No stored responses for this survey.
      </div>
    );
  }

  return (
    <>
      {selected && (
        <ResponseDetail
          response={selected}
          questionMap={questionMap}
          onClose={() => setSelected(null)}
        />
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Response ID</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">SIS ID</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Match Status</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Date Submitted</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs uppercase tracking-wide">Answers</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {responses.map((r) => (
              <tr
                key={r.id}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => setSelected(r)}
              >
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.smResponseId || r.id}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">
                  {r.sisId || <span className="text-slate-300 italic">—</span>}
                </td>
                <td className="px-4 py-3">
                  <MatchBadge status={r.matchStatus} />
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(r.dateCreated)}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">
                  {r.answers ? Object.keys(r.answers).length : 0} questions
                </td>
                <td className="px-4 py-3 text-slate-300">
                  <ChevronRight size={14} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-2 text-center">Click any row to view full response</p>
    </>
  );
}

// ─── Compute analytics from all responses ─────────────────────────────────
function buildAnalytics(responses, questionMap) {
  // Per question: tally choice counts across all responses
  // Result: { [qId]: { heading, type, rows: {rowLabel: {choiceLabel: count}}, choices: {choiceLabel: count} } }
  const stats = {};

  for (const r of responses) {
    if (!r.answers) continue;
    for (const [qId, answerArr] of Object.entries(r.answers)) {
      const q = questionMap[qId] || {};
      if (!stats[qId]) {
        stats[qId] = {
          heading: q.heading || qId,
          isMatrix: q.type === "matrix" || (answerArr || []).some(a => a.row_id && a.choice_id),
          rows: {},    // rowLabel → { choiceLabel → count }
          choices: {}, // choiceLabel → count (non-matrix)
          total: 0,
        };
      }
      const s = stats[qId];
      s.total++;
      for (const a of (answerArr || [])) {
        if (s.isMatrix) {
          const rowLabel = (a.row_id && q.rows?.[a.row_id]) || a.row_id || "?";
          const choiceLabel = (a.choice_id && q.choices?.[a.choice_id]) || a.text || "?";
          if (!s.rows[rowLabel]) s.rows[rowLabel] = {};
          s.rows[rowLabel][choiceLabel] = (s.rows[rowLabel][choiceLabel] || 0) + 1;
        } else {
          const choiceLabel = (a.choice_id && q.choices?.[a.choice_id]) || a.text || "?";
          s.choices[choiceLabel] = (s.choices[choiceLabel] || 0) + 1;
        }
      }
    }
  }
  return stats;
}

// Ordered scale so bars render in a sensible sequence
const SCALE_ORDER = ["Always", "Often", "Sometimes", "Rarely", "Never",
  "Strongly Agree", "Agree", "Neutral", "Disagree", "Strongly Disagree",
  "Very Satisfied", "Satisfied", "Unsatisfied", "Very Unsatisfied",
  "Excellent", "Good", "Fair", "Poor"];

const SCALE_COLORS = {
  "Always": "#16a34a", "Often": "#4ade80", "Sometimes": "#facc15",
  "Rarely": "#fb923c", "Never": "#ef4444",
  "Strongly Agree": "#16a34a", "Agree": "#4ade80", "Neutral": "#94a3b8",
  "Disagree": "#fb923c", "Strongly Disagree": "#ef4444",
  "Very Satisfied": "#16a34a", "Satisfied": "#4ade80",
  "Unsatisfied": "#fb923c", "Very Unsatisfied": "#ef4444",
  "Excellent": "#16a34a", "Good": "#4ade80", "Fair": "#fb923c", "Poor": "#ef4444",
};
const DEFAULT_COLOR = "#6366f1";

function sortChoices(choices) {
  const keys = Object.keys(choices);
  const ordered = SCALE_ORDER.filter(k => keys.includes(k));
  const rest = keys.filter(k => !ordered.includes(k)).sort();
  return [...ordered, ...rest];
}

// ─── Analytics: horizontal stacked bar for a matrix row ───────────────────
function MatrixRowBar({ rowLabel, choiceCounts, totalResponses }) {
  const sorted = sortChoices(choiceCounts);
  const rowTotal = Object.values(choiceCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-600 flex-1 pr-4">{rowLabel}</span>
        <span className="text-xs text-slate-400 shrink-0">{rowTotal} responses</span>
      </div>
      {/* Stacked bar */}
      <div className="flex h-6 rounded overflow-hidden w-full bg-slate-100">
        {sorted.map(label => {
          const count = choiceCounts[label] || 0;
          const pct = rowTotal > 0 ? (count / rowTotal) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={label}
              title={`${label}: ${count} (${Math.round(pct)}%)`}
              style={{ width: `${pct}%`, background: SCALE_COLORS[label] || DEFAULT_COLOR }}
              className="transition-all"
            />
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
        {sorted.map(label => {
          const count = choiceCounts[label] || 0;
          const pct = rowTotal > 0 ? Math.round((count / rowTotal) * 100) : 0;
          return (
            <span key={label} className="flex items-center gap-1 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-sm inline-block shrink-0" style={{ background: SCALE_COLORS[label] || DEFAULT_COLOR }} />
              {label} {pct}%
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Analytics: simple horizontal bar for choice questions ────────────────
function ChoiceBar({ label, count, max }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs text-slate-700 flex-1 pr-4 truncate">{label}</span>
        <span className="text-xs font-medium text-slate-600 shrink-0">{count}</span>
      </div>
      <div className="h-4 bg-slate-100 rounded overflow-hidden">
        <div
          className="h-full rounded transition-all"
          style={{ width: `${pct}%`, background: SCALE_COLORS[label] || DEFAULT_COLOR }}
        />
      </div>
    </div>
  );
}

// ─── Full analytics view ───────────────────────────────────────────────────
function AnalyticsView({ responses, questionMap }) {
  if (!responses || responses.length === 0) {
    return <p className="text-sm text-slate-400 text-center py-12">No responses to analyze.</p>;
  }

  const stats = buildAnalytics(responses, questionMap);
  const qIds = Object.keys(stats);

  if (qIds.length === 0) {
    return <p className="text-sm text-slate-400 text-center py-12">No decodable answers — question map may be missing.</p>;
  }

  return (
    <div className="space-y-6">
      {qIds.map(qId => {
        const s = stats[qId];
        return (
          <div key={qId} className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-slate-800 mb-4">{s.heading}</p>
            {s.isMatrix ? (
              Object.entries(s.rows).map(([rowLabel, choiceCounts]) => (
                <MatrixRowBar
                  key={rowLabel}
                  rowLabel={rowLabel}
                  choiceCounts={choiceCounts}
                  totalResponses={responses.length}
                />
              ))
            ) : (
              (() => {
                const sorted = sortChoices(s.choices);
                const max = Math.max(...sorted.map(l => s.choices[l] || 0));
                return sorted.map(label => (
                  <ChoiceBar key={label} label={label} count={s.choices[label] || 0} max={max} />
                ));
              })()
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Survey detail view ────────────────────────────────────────────────────
function SurveyDetail({ survey, onBack }) {
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [responses, setResponses] = useState(null);
  const [error, setError] = useState(null);
  const [detailTab, setDetailTab] = useState("overview"); // "overview" | "responses"
  const questionMap = survey.questionMap || {};

  const loadStoredResponses = useCallback(async () => {
    setLoadingResponses(true);
    try {
      const { data } = await smGetStoredResponses({ surveyId: survey.id });
      setResponses(data);
    } catch (e) {
      setError(e.message || "Failed to load stored responses.");
    } finally {
      setLoadingResponses(false);
    }
  }, [survey.id]);

  useEffect(() => { loadStoredResponses(); }, [loadStoredResponses]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <button
          onClick={onBack}
          className="mt-0.5 p-1.5 rounded hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-800">{survey.title || survey.id}</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {responses ? responses.length.toLocaleString() : (survey.responseCount?.toLocaleString() ?? 0)} responses &middot;{" "}
            {survey.questionCount ?? Object.keys(questionMap).length} questions
          </p>
        </div>
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-center gap-1.5">
          <AlertCircle size={13} />
          API inactive
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {["overview", "responses"].map(t => (
          <button
            key={t}
            onClick={() => setDetailTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px capitalize ${
              detailTab === t
                ? "border-slate-800 text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            {t === "overview" ? "Overview" : `Responses${responses ? ` (${responses.length})` : ""}`}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-3 text-sm text-red-700 mb-4">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      {loadingResponses ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={18} />
          Loading responses…
        </div>
      ) : detailTab === "overview" ? (
        <AnalyticsView responses={responses} questionMap={questionMap} />
      ) : (
        <ResponseTable responses={responses} questionMap={questionMap} />
      )}
    </div>
  );
}

// ─── CSV Upload tab ────────────────────────────────────────────────────────
// entries: [{ file, status: "pending"|"uploading"|"done"|"error", result, error }]
function CSVUpload({ onIngestComplete }) {
  const [entries, setEntries] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);

  function addFiles(incoming) {
    const csvs = Array.from(incoming).filter(f => f.name.endsWith(".csv"));
    if (!csvs.length) return;
    setEntries(prev => {
      const existingNames = new Set(prev.map(e => e.file.name));
      const fresh = csvs
        .filter(f => !existingNames.has(f.name))
        .map(f => ({ file: f, status: "pending", result: null, error: null }));
      return [...prev, ...fresh];
    });
  }

  function removeEntry(fileName) {
    setEntries(prev => prev.filter(e => e.file.name !== fileName));
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const pending = entries.filter(e => e.status === "pending");
    if (!pending.length) return;
    setRunning(true);

    for (const entry of pending) {
      setEntries(prev => prev.map(e =>
        e.file.name === entry.file.name ? { ...e, status: "uploading" } : e
      ));
      try {
        const text = await entry.file.text();
        const { data } = await smIngestCSV({
          csvContent: text,
          fileName: entry.file.name,
        });
        setEntries(prev => prev.map(e =>
          e.file.name === entry.file.name ? { ...e, status: "done", result: data } : e
        ));
      } catch (err) {
        setEntries(prev => prev.map(e =>
          e.file.name === entry.file.name
            ? { ...e, status: "error", error: err.message || "Ingest failed" }
            : e
        ));
      }
    }

    setRunning(false);
    if (onIngestComplete) onIngestComplete();
  }

  const pendingCount = entries.filter(e => e.status === "pending").length;
  const allDone = entries.length > 0 && entries.every(e => e.status === "done");

  return (
    <div className="max-w-xl">
      <p className="text-sm text-slate-500 mb-6">
        Export surveys from SurveyMonkey as CSVs (Analyze → Export → All responses data → CSV).
        The filename becomes the survey name. Drop multiple files to batch ingest.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors mb-5 ${
          dragging ? "border-slate-400 bg-slate-50" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <input
          type="file"
          accept=".csv"
          multiple
          onChange={e => addFiles(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <UploadCloud size={28} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm text-slate-400">Drag and drop CSV files, or click to browse</p>
        <p className="text-xs text-slate-300 mt-1">Multiple files supported</p>
      </div>

      {/* File list */}
      {entries.length > 0 && (
        <div className="space-y-2 mb-5">
          {entries.map(entry => {
            const name = entry.file.name.replace(/\.csv$/i, "");
            const isDone = entry.status === "done";
            const isError = entry.status === "error";
            const isUploading = entry.status === "uploading";

            return (
              <div
                key={entry.file.name}
                className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
                  isDone ? "border-green-200 bg-green-50"
                  : isError ? "border-red-200 bg-red-50"
                  : isUploading ? "border-slate-200 bg-slate-50"
                  : "border-slate-200 bg-white"
                }`}
              >
                <span className="shrink-0 mt-0.5">
                  {isDone && <CheckCircle2 size={14} className="text-green-600" />}
                  {isError && <XCircle size={14} className="text-red-500" />}
                  {isUploading && <Loader2 size={14} className="animate-spin text-slate-400" />}
                  {entry.status === "pending" && <FileText size={14} className="text-slate-300" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium truncate ${
                    isDone ? "text-green-800" : isError ? "text-red-700" : "text-slate-700"
                  }`}>
                    {name}
                  </p>
                  {isDone && entry.result && (
                    <p className="text-xs text-green-700 mt-0.5">
                      {entry.result.processed} rows · {entry.result.matched} matched · {entry.result.unmatched} unmatched · {entry.result.noName} no name
                    </p>
                  )}
                  {isError && (
                    <p className="text-xs text-red-500 mt-0.5">{entry.error}</p>
                  )}
                </div>
                {!running && !isUploading && (
                  <button onClick={() => removeEntry(entry.file.name)} className="text-slate-300 hover:text-slate-500 shrink-0">
                    <X size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSubmit}
          disabled={running || pendingCount === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {running ? (
            <><Loader2 size={15} className="animate-spin" /> Ingesting…</>
          ) : allDone ? (
            <><CheckCircle2 size={15} /> All done</>
          ) : (
            <><UploadCloud size={15} /> {pendingCount > 0 ? `Ingest ${pendingCount} file${pendingCount !== 1 ? "s" : ""}` : "Ingest"}</>
          )}
        </button>
        {entries.length > 0 && !running && (
          <button onClick={() => setEntries([])} className="text-sm text-slate-400 hover:text-slate-600">
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
export default function SurveyMonkeyTool() {
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [tab, setTab] = useState("surveys"); // "surveys" | "upload" | "log"

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Database size={20} className="text-slate-500" />
          <h1 className="text-2xl font-bold text-slate-900">SurveyMonkey</h1>
          <span className="ml-2 text-xs font-medium bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
            Super Admin
          </span>
        </div>
        <p className="text-slate-500 text-sm">
          Viewing previously ingested surveys stored in Firestore. SurveyMonkey API access is no longer active.
        </p>
      </div>

      {/* Tabs — only show when not drilling into a survey */}
      {!selectedSurvey && (
        <div className="flex gap-1 border-b border-slate-200 mb-6">
          <button
            onClick={() => setTab("surveys")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === "surveys"
                ? "border-slate-800 text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            Surveys
          </button>
          <button
            onClick={() => setTab("upload")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5 ${
              tab === "upload"
                ? "border-slate-800 text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            <UploadCloud size={14} />
            Upload CSV
          </button>
          <button
            onClick={() => setTab("log")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5 ${
              tab === "log"
                ? "border-slate-800 text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            <ClipboardList size={14} />
            Ingest Log
          </button>
        </div>
      )}

      {/* Content */}
      {selectedSurvey ? (
        <SurveyDetail
          survey={selectedSurvey}
          onBack={() => setSelectedSurvey(null)}
        />
      ) : tab === "surveys" ? (
        <SurveyList onSelectSurvey={setSelectedSurvey} />
      ) : tab === "upload" ? (
        <CSVUpload onIngestComplete={() => setTab("surveys")} />
      ) : (
        <IngestLog />
      )}
    </div>
  );
}